import {
	FormAddressLookup,
	httpService,
	type AddressFinderLookupItem,
	type FormAddressLookupProps,
	type LookupResults,
	type SearchOptions
} from "@sal/onevent-portal";
import { type LookupItem, type OnEventApiResponse } from "@origin/platform-types";
import { SystemName } from "../constants/systemname";
import { EMPTY_STRING } from "../utilities";

type BLLFormAddressLookupProps<T extends AddressFinderLookupItem, DisableClearable extends boolean>
	= Omit<FormAddressLookupProps<T, DisableClearable>, "getOptions"> & {
		freightPayerID?: number
	};

type LocationByAssociationAdditionalData = {
	address1?: string;
	address2?: string;
	code?: string;
	combinedCode?: string;
	contact?: string;
	email?: string;
	latitude?: number;
	locationType?: string;
	locationTypeID?: number;
	longitude?: number;
	mobile?: string;
	objectMeasureJSON?: string;
	parentLocationName?: string;
	phone?: string;
	postcode?: string;
	state?: string;
	suburb?: string;
	timeZoneID?: number;
};

export function BLLFormAddressLookup<T extends AddressFinderLookupItem, DisableClearable extends boolean>({
	freightPayerID,
	...props
}: BLLFormAddressLookupProps<T, DisableClearable>) {
	const options: SearchOptions = {
		systemName: "LocationByAssociation",
		additionalFilter: JSON.stringify({
			locationType: [SystemName.LocationType.Client],
			flagType: [SystemName.FlagType.LocationUserCreated],
			freightPayerID
		})
	};

	const getOptions = async (searchTerm?: string) => {
		if (!searchTerm || searchTerm === EMPTY_STRING)
			return Promise.resolve([]);

		const searchOptions = { ...options, searchTerm };
		const response: OnEventApiResponse<LookupResults<LookupItem<LocationByAssociationAdditionalData>>> =
			await httpService.post<SearchOptions, Array<LookupItem<LocationByAssociationAdditionalData>>>("Lookup/Search", searchOptions);

		const results = response?.data?.results ?? [];

		return results.map(r => {
			const address1 = r?.additionalData?.address1;
			const address2 = r?.additionalData?.address2;
			const suburb = r?.additionalData?.suburb;

			const formattedAddress = [address1, address2, suburb].filter(part => part && part !== EMPTY_STRING).join(", ")

			return {
				...r,
				additionalData: {
					...r?.additionalData,
					formattedAddress,
					locationName: r.name,
					displayName: r.name
				}
			};
		});
	};

	return (
		<FormAddressLookup<T, DisableClearable>
			{...props}
			getOptions={getOptions}
		/>
	);
}
